import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { MapPin, Users, Bookmark, ArrowLeft, Check } from 'lucide-react';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import { FiSearch, FiSend, FiChevronLeft, FiMessageSquare } from 'react-icons/fi';
import useResponsive from '../hooks/useResponsive';
import { AuthContext } from '../context/AuthContext';
import api from '../api/api';

const EmployerInboxPage = () => {
  const { conversationId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { user } = useContext(AuthContext);
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasFetchedRef = useRef(false);
  const hasProcessedQueryRef = useRef(false); // Track if we've processed query params
  const [conversations, setConversations] = useState([]);
  
  // Fetch conversations on mount and poll every 5 seconds
  useEffect(() => {
    const fetchConversations = async () => {
      try {
        setLoading(true);
        const response = await api.get('/conversations');
        setApiData(response.data);
        setError(null);
      } catch (err) {
        console.error('[ERROR] [Employer] Failed to fetch conversations:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    // Initial fetch
    fetchConversations();
    
    // Poll every 5 seconds for new messages
    const interval = setInterval(fetchConversations, 5000);
    
    // Cleanup on unmount
    return () => clearInterval(interval);
  }, []);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [showConversationList, setShowConversationList] = useState(!isMobile || !conversationId);
  const messagesEndRef = useRef(null);
  
  // Use real data from API
  useEffect(() => {
    if (apiData && apiData.data && apiData.data.conversations) {
      
      // Deduplicate and MERGE conversations - combine messages from duplicate conversations
      const conversationMap = new Map();
      const rawConversations = apiData.data.conversations;
      
      rawConversations.forEach(conv => {
        const key = `${conv.employerId}_${conv.candidateId}`;
        const existing = conversationMap.get(key);
        
        if (!existing) {
          // First conversation with this pair
          conversationMap.set(key, {...conv});
        } else {
          // Merge messages from duplicate conversation
          const existingMessages = existing.messages || [];
          const newMessages = conv.messages || [];
          
          // Combine messages and remove duplicates by message ID
          const messageMap = new Map();
          [...existingMessages, ...newMessages].forEach(msg => {
            messageMap.set(msg.id, msg);
          });
          // Sort by timestamp
          const allMessages = Array.from(messageMap.values()).sort((a, b) => 
            new Date(a.timestamp) - new Date(b.timestamp)
          );

          // Update the conversation with merged messages
          existing.messages = allMessages;
          existing.lastMessage = allMessages[allMessages.length - 1]?.text || existing.lastMessage;
          existing.lastMessageTime = allMessages[allMessages.length - 1]?.timestamp || existing.lastMessageTime;
          existing.unreadCount = allMessages.filter(m => !m.read).length;
          
          // Keep the NEWEST conversation ID (most recent activity)
          if (new Date(conv.lastMessageTime) > new Date(existing.lastMessageTime)) {
            existing.id = conv.id;
          }
        }
      });
      
      const mergedConversations = Array.from(conversationMap.values());
      setConversations(mergedConversations);
      
      // Update selected conversation if it exists in the new data
      if (selectedConversation) {
        const updatedSelected = mergedConversations.find(c => c.id === selectedConversation.id);
        if (updatedSelected && updatedSelected.messages.length !== selectedConversation.messages?.length) {
          setSelectedConversation(updatedSelected);
        }
      }
    } else if (apiData && !apiData.data?.conversations) {
      // If API data is not available, set empty conversations array
      setConversations([]);
    }
  }, [apiData]);

  // Ensure this page is only accessible to employer users
  useEffect(() => {
    // Check if user exists and has a userType
    if (user) {
      // If user is not an employer or recruiter, redirect to appropriate dashboard
      if (user.userType !== 'employer' && user.userType !== 'recruiter') {
        navigate('/jobs');
      }
    }
  }, [user, navigate]);

  // Handle new conversation from query params - separate useEffect
  useEffect(() => {
    const candidateId = searchParams.get('candidateId');
    const candidateName = searchParams.get('candidateName');
    
    // Only process if we have params, haven't processed them yet, AND conversations have loaded
    if (candidateId && candidateName && !hasProcessedQueryRef.current && !loading) {
      hasProcessedQueryRef.current = true; // Mark as processed immediately
      
      // Check if conversation with this candidate already exists
      const existingConversation = conversations.find(c => 
        c.participantId === candidateId || c.candidateId === candidateId
      );
      if (existingConversation) {
        setSelectedConversation(existingConversation);
        if (isMobile) {
          setShowConversationList(false);
        }
      } else {
        // Create a new conversation object
        const newConversation = {
          id: `new_${candidateId}`,
          participantId: candidateId,
          candidateId: candidateId,
          candidateName: decodeURIComponent(candidateName),
          lastMessage: 'Start a conversation',
          timestamp: new Date().toISOString(),
          unread: false,
          messages: [],
          isNew: true
        };
        
        // Add to conversations list and select it
        setConversations(prev => [newConversation, ...prev]);
        setSelectedConversation(newConversation);
        
        if (isMobile) {
          setShowConversationList(false);
        }
      }
      
      // Clear query params after a short delay to prevent re-triggering
      setTimeout(() => {
        const inboxPath = user?.userType === 'recruiter' ? '/recruiter/inbox' : '/inbox';
        navigate(inboxPath, { replace: true });
        // Reset the ref after navigation completes
        setTimeout(() => {
          hasProcessedQueryRef.current = false;
        }, 100);
      }, 100);
    }
  }, [searchParams, navigate, isMobile, conversations, loading, user]); // Dependencies for the effect
  
  // Handle conversation selection from URL
  useEffect(() => {
    // If there's a conversationId in the URL, find and select it
    if (conversationId) {
      const conversation = conversations.find(c => c.id.toString() === conversationId);
      if (conversation && conversation.id === conversationId) {
        setSelectedConversation(conversation);
        if (isMobile) {
          setShowConversationList(false);
        }
      }
    } else if (conversations.length > 0 && !selectedConversation) {
      // Auto-select first conversation if none selected
      setSelectedConversation(conversations[0]);
    }
  }, [conversationId, conversations, isMobile]);

  useEffect(() => {
    // Auto-scroll to the latest message
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedConversation?.messages]);

  const handleSelectConversation = (conversation) => {
    const inboxPath = user?.userType === 'recruiter' ? '/recruiter/inbox' : '/inbox';
    navigate(`${inboxPath}/${conversation.id}`);
    if (isMobile) {
      setShowConversationList(false);
    }
  };

  const handleDeleteConversation = async (conversationId) => {
    if (!window.confirm('Are you sure you want to delete this conversation? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/conversations/${conversationId}`);
      
      // Remove from local state
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      
      // If deleted conversation was selected, clear selection
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
        const inboxPath = user?.userType === 'recruiter' ? '/recruiter/inbox' : '/inbox';
        navigate(inboxPath);
      }
      
    } catch (err) {
      console.error('[ERROR] Failed to delete conversation:', err);
      alert('Failed to delete conversation. Please try again.');
    }
  };
  
  const handleBackToList = () => {
    setShowConversationList(true);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    const messageData = {
      conversationId: selectedConversation.id,
      content: newMessage,
      sender: user?.userType || 'employer'
    };
    
    // If it's a new conversation, include candidate name
    if (selectedConversation.isNew) {
      messageData.candidateName = selectedConversation.candidateName;
    }

    try {
      // Send message to backend
      const response = await api.post('/messages', messageData);
      
      if (response.data.success) {
        // Update local state with message from server
        const savedMessage = response.data.data.message;
        const updatedConversation = response.data.data.conversation;
        
        // Create updated conversation object
        const updatedConvo = {
          ...selectedConversation,
          id: updatedConversation.id, // Use real ID from backend
          isNew: false,
          messages: [...(selectedConversation.messages || []), savedMessage],
          lastMessage: newMessage,
          lastMessageTime: savedMessage.timestamp,
          timestamp: 'Just now'
        };
        
        // Update conversations array
        const updatedConversations = conversations.map(convo => {
          if (convo.id === selectedConversation.id) {
            return updatedConvo;
          }
          return convo;
        });
        setConversations(updatedConversations);
        
        // Update selected conversation with new message
        setSelectedConversation(updatedConvo);
        
        setNewMessage('');
      }
    } catch (err) {
      console.error('[ERROR] Failed to send message:', err);
      alert('Failed to send message. Please try again.');
    }
  };

  return (
    <div className="flex bg-white min-h-screen font-sans relative">
      <div className="fixed left-0 top-0 bottom-0 z-10">
        <Sidebar />
      </div>
      <main className="flex-1 flex flex-col h-screen overflow-hidden ml-0 md:ml-24">
        <div className="pt-4 px-4 md:pt-6 md:px-8 pb-4 bg-white shadow-sm">
          <DashboardHeader />
        </div>
        
        <div className="flex-grow flex flex-col md:flex-row overflow-hidden mx-4 md:mx-8 mt-6">
          {/* Conversation List - Sidebar on mobile, fixed width on desktop */}
          <div className={`${isMobile && !showConversationList ? 'hidden' : 'flex'} md:w-80 lg:w-96 bg-white flex-col md:max-h-full overflow-hidden rounded-lg md:mr-4 mb-4 md:mb-0 border border-gray-200`} 
            style={{ 
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), 2px 2px 0 rgba(0, 0, 0, 0.8)',
              transform: 'translateX(-2px) translateY(-2px)'
            }}>
            <div className="p-5 border-b">
              <h2 className="text-xl font-bold text-black mb-4">Employer Inbox</h2>
              <div className="relative">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-black" size={18} />
                <input 
                  type="text" 
                  placeholder="Search messages..." 
                  className="w-full pl-12 pr-4 py-3 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-black focus:ring-opacity-50 transition-all" 
                  style={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)' }}
                />
              </div>
            </div>
            <div className="flex-grow overflow-y-auto">
              {conversations.length > 0 ? (
                conversations.map(convo => (
                  <ConversationItem 
                    key={convo.id} 
                    conversation={convo} 
                    isSelected={selectedConversation?.id === convo.id}
                    onSelect={() => handleSelectConversation(convo)}
                  />
                ))
              ) : (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FiMessageSquare size={24} className="text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">No Messages Yet</h3>
                  <p className="text-gray-500 mb-6">You haven't started any conversations with candidates.</p>
                  <p className="text-sm text-gray-500">When you find candidates you're interested in, you can message them directly from their profile.</p>
                </div>
              )}
            </div>
          </div>

          {/* Message View - Takes remaining space */}
          <div className={`${isMobile && showConversationList ? 'hidden' : 'flex'} flex-1 flex-col bg-white ml-0 rounded-lg transform transition-all border border-gray-200`} 
            style={{ 
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), 2px 2px 0 rgba(0, 0, 0, 0.8)',
              transform: 'translateX(-2px) translateY(-2px)'
            }}>
            {selectedConversation ? (
              <>
                {/* Conversation Header */}
                <div className="p-4 md:p-5 border-b flex items-center gap-4 bg-white">
                  {isMobile && (
                    <button 
                      onClick={handleBackToList}
                      className="mr-2 p-1 rounded-full hover:bg-gray-100"
                    >
                      <FiChevronLeft size={20} />
                    </button>
                  )}
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-black">{selectedConversation.candidateName}</h3>
                    <div className="flex items-center">
                      <span className="w-2 h-2 bg-black mr-2"></span>
                      <p className="text-sm text-black">{selectedConversation.jobTitle}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => handleDeleteConversation(selectedConversation.id)}
                      className="p-2 text-red-600 hover:bg-red-50 transition-colors rounded-md"
                      title="Delete conversation"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
                
                {/* Messages Container */}
                <div className="flex-grow p-4 md:p-6 overflow-y-auto bg-white space-y-4 md:space-y-5">
                  {selectedConversation.messages && selectedConversation.messages.length > 0 ? (
                    selectedConversation.messages.map((msg, index) => (
                      <Message 
                        key={index} 
                        message={msg} 
                        currentUserId={user?.id}
                        isFirstMessage={index === 0}
                        isLastMessage={index === selectedConversation.messages.length - 1}
                      />
                    ))
                  ) : (
                    <div className="text-center py-12 px-6">
                      <div className="bg-white p-8 border border-black max-w-md mx-auto">
                        <div className="bg-white border border-black p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-bold text-black mb-2">Start the conversation</h3>
                        <p className="text-black">Send your first message to begin chatting with {selectedConversation.candidateName}.</p>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
                
                {/* Message Input */}
                <div className="p-3 md:p-4 bg-white border-t">
                  <form onSubmit={handleSendMessage} className="flex items-center gap-2 md:gap-3">
                    <div className="flex-grow relative">
                      <input 
                        type="text" 
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message..." 
                        className="w-full border border-black py-3 px-5 pr-12 focus:outline-none transition-all"
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex gap-1">
                        <button type="button" className="text-black p-1">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                        <button type="button" className="text-black p-1">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <button 
                      type="submit" 
                      disabled={!newMessage.trim()}
                      className="bg-black text-white p-3 border border-black disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      <FiSend size={18} />
                    </button>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-black flex-col p-8">
                <div className="bg-white border border-black p-8 mb-6">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-black mb-2">Your messages</h3>
                <p className="text-black text-center max-w-sm">Select a conversation from the list to start chatting with candidates.</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

const ConversationItem = ({ conversation, isSelected, onSelect }) => {
  const hasUnread = conversation.unread > 0;
  
  return (
    <div 
      onClick={onSelect} 
      className={`p-4 flex items-center gap-4 cursor-pointer transition-all duration-200 border-b border-black ${isSelected ? 'bg-black text-white' : 'hover:bg-gray-100'}
        ${hasUnread && !isSelected ? 'bg-gray-100' : ''}`
      }
    >
      <div className="flex-grow overflow-hidden">
        <div className="flex justify-between items-center">
          <p className={`font-bold truncate ${isSelected ? 'text-white' : hasUnread ? 'text-black' : 'text-black'}`}>
            {conversation.candidateName}
          </p>
          <p className={`text-xs ${isSelected ? 'text-white' : hasUnread ? 'bg-black text-white px-2 py-1' : 'text-black'}`}>
            {conversation.timestamp}
          </p>
        </div>
        <div className="flex justify-between items-center mt-1">
          <p className={`text-sm truncate ${isSelected ? 'text-white' : hasUnread ? 'text-black font-medium' : 'text-black'}`}>
            {conversation.lastMessage}
          </p>
          {hasUnread && !isSelected && (
            <span className="w-2 h-2 bg-black flex-shrink-0 ml-2"></span>
          )}
        </div>
        <div className="flex items-center mt-1">
          <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 ${isSelected ? 'text-white' : 'text-black'} mr-1`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <p className={`text-xs ${isSelected ? 'text-white' : 'text-black'} truncate`}>{conversation.jobTitle}</p>
        </div>
      </div>
    </div>
  );
};

const Message = ({ message, currentUserId }) => {
  // Check if the current user sent this message
  const isSentByMe = message.senderId === currentUserId;
  
  return (
    <div className={`flex ${isSentByMe ? 'justify-end' : 'justify-start'}`}>
      <div 
        className={`relative max-w-xs sm:max-w-md md:max-w-lg px-5 py-3 ${isSentByMe ? 'bg-black text-white border border-black mr-2' : 'bg-white text-black border border-black ml-2'}
        `}
      >
        <p className="whitespace-pre-wrap break-words">{message.text}</p>
        <span 
          className={`text-xs block mt-2 ${isSentByMe ? 'text-right text-white' : 'text-black'}`}
        >
          {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    </div>
  );
};

export default EmployerInboxPage;
